// Default React import: the Jest babel config uses the classic JSX runtime
import React, { useEffect, useState } from "react";
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import type { CreateMenuItem } from "./types";

interface CreateRecordMenuProps {
  items: CreateMenuItem[];
  selectedRowCount: number;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

function isDisabled(item: CreateMenuItem, selectedRowCount: number) {
  return Boolean(
    (item.requiresRowSelection && selectedRowCount !== 1) ||
      (item.disabledWhenMultipleSelected && selectedRowCount > 1)
  );
}

function LeafItem({
  item,
  selectedRowCount,
  onPick,
  onMouseEnter,
}: {
  item: CreateMenuItem;
  selectedRowCount: number;
  onPick: (item: CreateMenuItem) => void;
  onMouseEnter?: () => void;
}) {
  const disabled = isDisabled(item, selectedRowCount);
  return (
    <MenuItem
      disabled={disabled}
      onClick={() => onPick(item)}
      onMouseEnter={onMouseEnter}
    >
      {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
      <ListItemText>
        {item.label}
        {disabled && item.disabledReason && (
          <Typography variant="caption" display="block" color="text.secondary">
            ({item.disabledReason})
          </Typography>
        )}
      </ListItemText>
    </MenuItem>
  );
}

/** A group: its children open beside it while the pointer is on it or on
 *  the submenu, like the grids' context menus' submenus. */
function GroupItem({
  item,
  selectedRowCount,
  open,
  onOpen,
  onCloseGroup,
  onPick,
}: {
  item: CreateMenuItem;
  selectedRowCount: number;
  open: boolean;
  onOpen: (anchor: HTMLElement) => void;
  onCloseGroup: () => void;
  onPick: (item: CreateMenuItem) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const show = (el: HTMLElement) => {
    setAnchorEl(el);
    onOpen(el);
  };
  return (
    <>
      <MenuItem
        onMouseEnter={(e) => show(e.currentTarget)}
        onClick={(e) => show(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
        <ListItemText>{item.label}</ListItemText>
        <ChevronRightIcon fontSize="small" />
      </MenuItem>
      <Menu
        open={open && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onCloseGroup}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // Only the submenu's own surface should catch the pointer; the root
        // menu stays interactive underneath (its items close this group)
        slotProps={{
          root: { sx: { pointerEvents: "none" } },
          paper: { sx: { pointerEvents: "auto" }, onMouseLeave: onCloseGroup },
        }}
        autoFocus={false}
        disableAutoFocus
        disableEnforceFocus
      >
        {(item.children ?? []).map((child, i) => (
          <LeafItem
            key={i}
            item={child}
            selectedRowCount={selectedRowCount}
            onPick={onPick}
          />
        ))}
      </Menu>
    </>
  );
}

/**
 * The toolbar's Add Record dropdown. An item with `children` is a group that
 * opens a nested menu on hover; everything else runs its `onClick`.
 */
export function CreateRecordMenu({
  items,
  selectedRowCount,
  anchorEl,
  open,
  onClose,
}: CreateRecordMenuProps) {
  // Index of the group whose submenu is open; entering another root item
  // closes it
  const [openGroup, setOpenGroup] = useState<number | null>(null);
  useEffect(() => {
    if (!open) setOpenGroup(null);
  }, [open]);

  const pick = (item: CreateMenuItem) => {
    setOpenGroup(null);
    onClose();
    item.onClick?.();
  };
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {items.map((item, i) =>
        item.children ? (
          <GroupItem
            key={i}
            item={item}
            selectedRowCount={selectedRowCount}
            open={openGroup === i}
            onOpen={() => setOpenGroup(i)}
            onCloseGroup={() => setOpenGroup(null)}
            onPick={pick}
          />
        ) : (
          <LeafItem
            key={i}
            item={item}
            selectedRowCount={selectedRowCount}
            onPick={pick}
            onMouseEnter={() => setOpenGroup(null)}
          />
        )
      )}
    </Menu>
  );
}
